import { Helmet } from "react-helmet-async";

interface SEOData {
  title: string;
  description: string;
  keywords: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  structuredData?: object;
  noIndex?: boolean;
}

interface SEOManagerProps {
  data: SEOData;
  page: string;
}

export default function SEOManager({ data, page }: SEOManagerProps) {
  // Get current URL dynamically
  const currentUrl = typeof window !== 'undefined'
    ? `https://innodot.tech${window.location.pathname}${window.location.search}`
    : `https://innodot.tech/${page}`;

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{data.title}</title>
      <meta name="description" content={data.description} />
      <meta name="keywords" content={data.keywords} />
      <meta
        name="robots"
        content={data.noIndex ? "noindex, nofollow" : "index, follow"}
      />
      <meta name="author" content="Inno Dot - Digital Marketing Agency" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#000000" />

      {/* Open Graph tags */}
      <meta property="og:title" content={data.ogTitle || data.title} />
      <meta
        property="og:description"
        content={data.ogDescription || data.description}
      />
      <meta
        property="og:image"
        content={data.ogImage || "https://innodot.tech/og-image.jpg"}
      />
      <meta
        property="og:url"
        content={data.ogUrl || currentUrl}
      />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Inno Dot - Digital Marketing Agency" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@innodot" />
      <meta name="twitter:title" content={data.twitterTitle || data.title} />
      <meta
        name="twitter:description"
        content={data.twitterDescription || data.description}
      />
      <meta
        name="twitter:image"
        content={data.twitterImage || data.ogImage || "https://innodot.tech/twitter-image.jpg"}
      />

      {/* Additional SEO meta tags */}
      <meta name="application-name" content="Inno Dot" />
      <meta name="apple-mobile-web-app-title" content="Inno Dot" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="msapplication-TileColor" content="#000000" />
      <meta name="msapplication-config" content="/browserconfig.xml" />

      {/* Canonical URL */}
      <link rel="canonical" href={data.canonicalUrl || currentUrl} />

      {/* Preconnect links for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="preconnect" href="https://images.unsplash.com" />

      {/* Structured data */}
      {data.structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(data.structuredData)}
        </script>
      )}
    </Helmet>
  );
}

// SEO data for all pages
export const seoData = {
  home: {
    title: "Inno Dot - Digital Marketing & AI Agency | 10x Growth",
    description:
      "Drive 10x business growth with AI-powered digital marketing. Proven 350% ROI results. Expert SEO, PPC, social media & automation services for 200+ businesses.",
    keywords:
      "digital marketing agency, AI agency, 10x growth, business growth, lead generation, SEO services, PPC advertising, social media marketing, marketing automation, digital transformation, best SEO services for small businesses, AI-powered digital marketing solutions, PPC advertising agency for lead generation, social media marketing for B2B companies, email marketing automation services, web design and development agency, AI automation for business processes, digital marketing agency with guaranteed ROI, full-service digital marketing company, data-driven digital marketing strategies, AI marketing automation agency, chatbot development services, AI-powered lead generation systems, automated marketing solutions, digital marketing agency Morgan Hill CA, SEO services Morgan Hill California, PPC advertising company Morgan Hill, social media marketing Morgan Hill CA, web design agency Morgan Hill, AI automation services Morgan Hill, Silicon Valley digital marketing agency, San Francisco Bay Area SEO services, Bay Area PPC advertising company, San Jose digital marketing agency, Palo Alto social media marketing, Bay Area AI automation services, California digital marketing agency, best SEO company California, digital marketing services Northern California, California AI marketing automation",
    canonicalUrl: "https://innodot.tech/",
    ogTitle: "Digital Marketing & AI Agency for 10x Business Growth",
    ogDescription:
      "Transform your business with cutting-edge digital marketing and AI automation. We help businesses achieve 10x growth with proven 350% ROI results.",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Inno Dot",
        description:
          "Premier digital marketing agency specializing in SEO, PPC, social media marketing, web design, and AI automation",
        url: "https://innodot.tech",
        logo: "https://innodot.tech/logo.png",
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+1-555-123-4567",
          contactType: "customer service",
          availableLanguage: "English",
        },
        address: {
          "@type": "PostalAddress",
          streetAddress: "Morgan Hill",
          addressLocality: "Morgan Hill",
          addressRegion: "CA",
          postalCode: "95037",
          addressCountry: "US",
        },
        sameAs: [
          "https://facebook.com/innodot",
          "https://twitter.com/innodot",
          "https://linkedin.com/company/innodot",
          "https://instagram.com/innodot",
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What makes Inno Dot different from other digital marketing agencies?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "We combine traditional digital marketing expertise with cutting-edge AI automation. Our data-driven approach, 24/7 support, and focus on measurable results sets us apart. We don't just run campaigns - we transform businesses through strategic online marketing and performance advertising.",
            },
          },
          {
            "@type": "Question",
            name: "How quickly can I expect to see results?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Most clients see initial improvements within 30 days, with significant results typically visible within 60-90 days. However, timelines vary based on industry, competition, and current digital presence. We provide weekly reports to track progress.",
            },
          },
          {
            "@type": "Question",
            name: "What's included in your AI Automation services?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Our AI Automation includes intelligent chatbots, lead qualification systems, email marketing automation, social media management, predictive analytics, and custom workflow automation. We also provide comprehensive training and continuous optimization for maximum ROI.",
            },
          },
          {
            "@type": "Question",
            name: "Do you work with businesses in my industry?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "We've successfully worked with businesses across 50+ industries including healthcare, e-commerce, SaaS, professional services, and more. Our strategies are customized for each industry's unique challenges and opportunities.",
            },
          },
        ],
      },
    ],
  },
  services: {
    title: "Digital Marketing Services | SEO, PPC, Social Media | Inno Dot",
    description:
      "Expert digital marketing services: SEO, PPC, social media, content marketing, email campaigns, web design & AI automation. Grow your business with proven strategies.",
    keywords:
      "digital marketing services, SEO optimization, PPC management, social media marketing, content marketing, email marketing, web development, AI automation, digital strategy services",
    canonicalUrl: "https://innodot.tech/services",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Digital Marketing Services",
        description:
          "Comprehensive digital marketing solutions including SEO, PPC, social media, and AI automation",
        provider: {
          "@type": "Organization",
          name: "Inno Dot",
        },
        serviceType: "Digital Marketing",
        areaServed: "Worldwide",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: "https://innodot.tech/services",
          },
        ],
      },
    ],
  },
  portfolio: {
    title: "Our Work - Digital Marketing Success Stories | Inno Dot",
    description:
      "Real digital marketing success stories & case studies. See how we boost traffic, conversions & ROI for businesses. Proven results that drive growth.",
    keywords:
      "digital marketing portfolio, case studies, marketing results, success stories, client testimonials, proven strategies, digital marketing examples, campaign results",
    canonicalUrl: "https://innodot.tech/portfolio",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        name: "Digital Marketing Portfolio",
        description:
          "Showcase of successful digital marketing campaigns and case studies",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Portfolio",
            item: "https://innodot.tech/portfolio",
          },
        ],
      },
    ],
  },
  pricing: {
    title: "Digital Marketing Pricing | Inno Dot",
    description:
      "Discover affordable digital marketing pricing plans tailored for your business. Maximize ROI with expert SEO, PPC & social media services. Get started today!",
    keywords:
      "digital marketing pricing, marketing packages, SEO pricing, PPC management cost, social media marketing prices, affordable digital marketing, marketing agency rates",
    canonicalUrl: "https://innodot.tech/pricing",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "Digital Marketing Services",
        description:
          "Professional digital marketing packages for businesses of all sizes",
        brand: {
          "@type": "Brand",
          name: "Inno Dot",
        },
        offers: [
          {
            "@type": "Offer",
            name: "Startup Accelerator",
            price: "999",
            priceCurrency: "USD",
            priceValidUntil: "2025-12-31",
            availability: "https://schema.org/InStock",
          },
          {
            "@type": "Offer",
            name: "Growth Catalyst",
            price: "2199",
            priceCurrency: "USD",
            priceValidUntil: "2025-12-31",
            availability: "https://schema.org/InStock",
          },
          {
            "@type": "Offer",
            name: "Enterprise Domination",
            price: "4499",
            priceCurrency: "USD",
            priceValidUntil: "2025-12-31",
            availability: "https://schema.org/InStock",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Pricing",
            item: "https://innodot.tech/pricing",
          },
        ],
      },
    ],
  },
  seo: {
    title: "SEO Services - Boost Your Search Rankings | Inno Dot",
    description:
      "Professional SEO services to boost search rankings. Technical SEO, keywords, content strategy & link building. Attract ideal customers & grow traffic.",
    keywords:
      "SEO services, search engine optimization, technical SEO, keyword research, content optimization, link building, organic traffic, search rankings, SEO strategy, local SEO",
    canonicalUrl: "https://innodot.tech/services/seo",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Search Engine Optimization (SEO) Services",
        description:
          "Professional search engine optimization services for improved rankings and organic traffic growth",
        provider: {
          "@type": "Organization",
          name: "Inno Dot",
          url: "https://innodot.tech",
          logo: "https://innodot.tech/logo.png",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Morgan Hill",
            addressLocality: "Morgan Hill",
            addressRegion: "CA",
            postalCode: "95037",
            addressCountry: "US",
          },
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+1-555-123-4567",
            contactType: "customer service",
            availableLanguage: "English",
          },
        },
        serviceType: "Digital Marketing",
        areaServed: {
          "@type": "Country",
          name: "United States",
        },
        offers: {
          "@type": "Offer",
          name: "SEO Starter Package",
          price: "1299",
          priceCurrency: "USD",
          priceValidUntil: "2025-12-31",
          availability: "https://schema.org/InStock",
          description: "Essential SEO foundation for small businesses",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "150",
        },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "SEO Service Packages",
          itemListElement: [
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "SEO Starter",
                description: "Technical SEO audit & fixes, keyword research (25 keywords), on-page optimization (10 pages)",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "SEO Growth",
                description: "Everything in Starter plus advanced keyword research (50+ keywords), content strategy & creation, link building campaign",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "SEO Enterprise",
                description: "Everything in Growth plus unlimited keyword targeting, advanced technical optimizations, custom content marketing",
              },
            },
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: "https://innodot.tech/services",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "SEO Services",
            item: "https://innodot.tech/services/seo",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "SEO Services",
        description: "Professional SEO services to boost your search rankings",
        url: "https://innodot.tech/services/seo",
        mainEntity: {
          "@type": "Service",
          name: "Search Engine Optimization Services",
        },
      },
    ],
  },
  ppc: {
    title: "PPC Management - Maximize Your Ad Performance | Inno Dot",
    description:
      "Strategic PPC management for Google Ads & Facebook Ads. Optimize campaigns for better performance, lower costs & higher quality leads. Data-driven results.",
    keywords:
      "PPC management, Google Ads, Facebook Ads, pay per click advertising, PPC optimization, ad management, conversion optimization, ROAS improvement, paid search",
    canonicalUrl: "https://innodot.tech/services/ppc",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Pay-Per-Click (PPC) Management Services",
        description:
          "Strategic PPC management across Google Ads, Facebook Ads, and major platforms for maximum ROI",
        provider: {
          "@type": "Organization",
          name: "Inno Dot",
          url: "https://innodot.tech",
          logo: "https://innodot.tech/logo.png",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Morgan Hill",
            addressLocality: "Morgan Hill",
            addressRegion: "CA",
            postalCode: "95037",
            addressCountry: "US",
          },
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+1-555-123-4567",
            contactType: "customer service",
            availableLanguage: "English",
          },
        },
        serviceType: "Digital Advertising",
        areaServed: {
          "@type": "Country",
          name: "United States",
        },
        offers: {
          "@type": "Offer",
          name: "PPC Management Package",
          price: "899",
          priceCurrency: "USD",
          priceValidUntil: "2025-12-31",
          availability: "https://schema.org/InStock",
          description: "Professional PPC campaign management and optimization",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          reviewCount: "120",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: "https://innodot.tech/services",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "PPC Management",
            item: "https://innodot.tech/services/ppc",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "PPC Management Services",
        description: "Strategic PPC management for maximum ad performance",
        url: "https://innodot.tech/services/ppc",
        mainEntity: {
          "@type": "Service",
          name: "Pay-Per-Click Management Services",
        },
      },
    ],
  },
  "social-media": {
    title: "Social Media Marketing - Build Your Brand Community | Inno Dot",
    description:
      "Strategic social media marketing to grow your brand. Create engaging content, manage communities & run targeted campaigns. Connect with your audience effectively.",
    keywords:
      "social media marketing, social media management, content creation, community management, influencer marketing, social media strategy, engagement optimization",
    canonicalUrl: "https://innodot.tech/services/social-media",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Social Media Marketing Services",
        description:
          "Strategic social media marketing to grow your brand presence and community engagement",
        provider: {
          "@type": "Organization",
          name: "Inno Dot",
          url: "https://innodot.tech",
          logo: "https://innodot.tech/logo.png",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Morgan Hill",
            addressLocality: "Morgan Hill",
            addressRegion: "CA",
            postalCode: "95037",
            addressCountry: "US",
          },
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+1-555-123-4567",
            contactType: "customer service",
            availableLanguage: "English",
          },
        },
        serviceType: "Social Media Marketing",
        areaServed: {
          "@type": "Country",
          name: "United States",
        },
        offers: {
          "@type": "Offer",
          name: "Social Media Management Package",
          price: "799",
          priceCurrency: "USD",
          priceValidUntil: "2025-12-31",
          availability: "https://schema.org/InStock",
          description: "Comprehensive social media strategy and content creation",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "200",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: "https://innodot.tech/services",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Social Media Marketing",
            item: "https://innodot.tech/services/social-media",
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Social Media Marketing Services",
        description: "Strategic social media marketing to build your brand community",
        url: "https://innodot.tech/services/social-media",
        mainEntity: {
          "@type": "Service",
          name: "Social Media Marketing Services",
        },
      },
    ],
  },
  "email-marketing": {
    title: "Email Marketing - Connect & Convert Customers | Inno Dot",
    description:
      "Strategic email marketing that builds relationships & drives conversions. Implement automation, segmentation & personalization. Maximize email performance.",
    keywords:
      "email marketing, email automation, email campaigns, newsletter marketing, email segmentation, marketing automation, drip campaigns, email optimization",
    canonicalUrl: "https://innodot.tech/services/email-marketing",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Email Marketing Services",
        description: "Professional email marketing and automation services",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: "https://innodot.tech/services",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Email Marketing",
            item: "https://innodot.tech/email-marketing",
          },
        ],
      },
    ],
  },
  "web-design": {
    title: "Web Design & Development - Stunning & Effective | Inno Dot",
    description:
      "Professional web design & development that converts visitors. Create responsive, user-friendly websites with optimized UX/UI. Fast performance & mobile-first design.",
    keywords:
      "web design, web development, responsive design, UX/UI design, website optimization, mobile-first design, conversion optimization, website performance",
    canonicalUrl: "https://innodot.tech/services/web-design",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Web Design & Development",
        description: "Professional web design and development services",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: "https://innodot.tech/services",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Web Design & Development",
            item: "https://innodot.tech/web-design",
          },
        ],
      },
    ],
  },
  "brand-strategy": {
    title:
      "Brand Strategy & Design - Define Your Brand Identity | Inno Dot",
    description:
      "Strategic brand development that sets you apart. We craft compelling brand identities through logo design, messaging, positioning, and comprehensive brand guidelines that resonate with your target audience.",
    keywords:
      "brand strategy, brand design, logo design, brand identity, brand positioning, brand messaging, visual identity, brand guidelines, rebranding services",
    canonicalUrl: "https://innodot.tech/services/brand-strategy",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Brand Strategy & Design",
        description: "Professional brand strategy and design services",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: "https://innodot.tech/services",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Brand Strategy & Design",
            item: "https://innodot.tech/brand-strategy",
          },
        ],
      },
    ],
  },
  "ai-automation": {
    title: "AI Automation Services | Business Process Automation | Inno Dot",
    description:
      "Cutting-edge AI automation services that save 30+ hours weekly. Chatbots, workflow automation, predictive analytics & intelligent business solutions. Transform your operations.",
    keywords:
      "AI automation, artificial intelligence, business automation, chatbots, workflow automation, process automation, AI solutions, machine learning, predictive analytics",
    canonicalUrl: "https://innodot.tech/services/ai-automation",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "AI Automation Services",
        description:
          "Advanced AI and automation solutions for business optimization",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: "https://innodot.tech/services",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "AI Automation",
            item: "https://innodot.tech/ai-automation",
          },
        ],
      },
    ],
  },
  "privacy-policy": {
    title: "Privacy Policy | Data Protection & Security | Inno Dot",
    description:
      "Learn how Inno Dot protects your privacy & personal information. Comprehensive privacy policy covering data collection, usage, security measures & your rights. GDPR compliant.",
    keywords:
      "privacy policy, data protection, GDPR compliance, data security, personal information, privacy rights, data collection, information security",
    canonicalUrl: "https://innodot.tech/privacy-policy",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Privacy Policy",
        description:
          "Comprehensive privacy policy detailing data protection practices and user rights",
        url: "https://innodot.tech/privacy-policy",
        publisher: {
          "@type": "Organization",
          name: "Inno Dot",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Privacy Policy",
            item: "https://innodot.tech/privacy-policy",
          },
        ],
      },
    ],
  },
  contact: {
    title: "Contact Us | Get In Touch with Inno Dot Digital Marketing Agency",
    description:
      "Ready to grow your business? Contact Inno Dot for expert digital marketing services: SEO, PPC, social media, web design & AI automation. Get your free consultation today.",
    keywords:
      "contact us digital marketing agency, get free consultation, digital marketing services, SEO services, PPC advertising, social media marketing, web design, AI automation, business growth",
    canonicalUrl: "https://innodot.tech/contact",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact Us",
        description:
          "Contact Inno Dot for expert digital marketing services and free consultation",
        url: "https://innodot.tech/contact",
        mainEntity: {
          "@type": "Organization",
          name: "Inno Dot",
          description: "Digital Marketing Agency",
          url: "https://innodot.tech",
          logo: "https://innodot.tech/logo.png",
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+1-555-123-4567",
            contactType: "customer service",
            availableLanguage: "English",
          },
          address: {
            "@type": "PostalAddress",
            streetAddress: "123 Innovation Drive",
            addressLocality: "Tech City",
            addressRegion: "TC",
            postalCode: "12345",
            addressCountry: "US",
          },
          sameAs: [
            "https://facebook.com/innodot",
            "https://twitter.com/innodot",
            "https://linkedin.com/company/innodot",
            "https://instagram.com/innodot",
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://innodot.tech",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Contact Us",
            item: "https://innodot.tech/contact",
          },
        ],
      },
    ],
  },
  "service-detail": {
    title: "Digital Marketing Services | Expert Solutions | Inno Dot",
    description:
      "Professional digital marketing services tailored to your business needs. Expert solutions in SEO, PPC, social media, content marketing & more. Drive growth today.",
    keywords:
      "digital marketing services, professional marketing, SEO services, PPC advertising, social media marketing, content marketing, business growth",
    canonicalUrl: "https://innodot.tech/services",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Digital Marketing Services",
        description:
          "Professional digital marketing services tailored to your business needs",
        provider: {
          "@type": "Organization",
          name: "Inno Dot",
          url: "https://innodot.tech",
        },
        serviceType: "Digital Marketing",
      },
    ],
  },
  "terms-conditions": {
    title: "Terms and Conditions | Inno Dot",
    description: "Read our terms and conditions for using Inno Dot's services and website.",
    keywords: "terms and conditions, legal, usage policy, service agreement",
    canonicalUrl: "https://innodot.tech/terms-conditions",
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Terms and Conditions",
        description: "Terms and Conditions for Inno Dot services",
      },
    ],
  },
};
